import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, ShieldCheck, ShieldOff, Smartphone, AlertTriangle, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Componente 2FA — pode ser embedado em qualquer página de perfil/configurações.
 * Mostra status + permite ativar (gera QR) ou desativar (com senha).
 */
export default function TwoFactorSetup() {
  const [status, setStatus] = useState(null); // {enabled}
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null); // {qr_code_data_uri, secret, otpauth_url}
  const [code, setCode] = useState('');
  const [enabling, setEnabling] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/auth/2fa/status`);
      setStatus(r.data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  const startSetup = async () => {
    try {
      const r = await axios.post(`${API}/auth/2fa/setup`);
      setSetupData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao iniciar 2FA');
    }
  };

  const confirmEnable = async () => {
    if (!code || code.length < 6) { toast.error('Digite o código de 6 dígitos'); return; }
    setEnabling(true);
    try {
      await axios.post(`${API}/auth/2fa/enable`, { code });
      toast.success('2FA ativado!');
      setSetupData(null);
      setCode('');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código inválido');
    } finally { setEnabling(false); }
  };

  const confirmDisable = async () => {
    if (!disablePassword) { toast.error('Digite sua senha'); return; }
    try {
      await axios.post(`${API}/auth/2fa/disable`, { password: disablePassword, code: disableCode || null });
      toast.success('2FA desativado');
      setShowDisableModal(false);
      setDisablePassword('');
      setDisableCode('');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao desativar');
    }
  };

  const copySecret = async () => {
    try { await navigator.clipboard.writeText(setupData.secret); toast.success('Secret copiado'); }
    catch (e) { toast.error('Não foi possível copiar'); }
  };

  if (loading) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4" data-testid="2fa-section">
      <div className="flex items-center gap-3">
        {status?.enabled ? <ShieldCheck className="w-6 h-6 text-green-600" /> : <Shield className="w-6 h-6 text-gray-400" />}
        <div className="flex-1">
          <h3 className="text-base font-bold">Autenticação de Dois Fatores (2FA)</h3>
          <p className="text-xs text-gray-500">
            {status?.enabled
              ? '✓ Ativado — você precisa do código do Google Authenticator para entrar.'
              : 'Adicione uma camada extra de segurança usando Google Authenticator, Authy ou 1Password.'}
          </p>
        </div>
        {status?.enabled ? (
          <button onClick={() => setShowDisableModal(true)} className="px-3 py-1.5 text-xs font-semibold bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-100" data-testid="btn-disable-2fa">
            <ShieldOff className="w-3.5 h-3.5 inline mr-1" /> Desativar
          </button>
        ) : !setupData && (
          <button onClick={startSetup} className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="btn-enable-2fa">
            <Smartphone className="w-3.5 h-3.5 inline mr-1" /> Ativar 2FA
          </button>
        )}
      </div>

      {/* Setup flow (QR + verify) */}
      {setupData && (
        <div className="border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/20 space-y-3" data-testid="2fa-setup-panel">
          <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">📱 Configure no seu app autenticador:</p>
          <ol className="text-xs text-blue-800 dark:text-blue-300 list-decimal pl-5 space-y-1">
            <li>Abra Google Authenticator (ou Authy/1Password)</li>
            <li>Toque em &ldquo;+&rdquo; e selecione &ldquo;Escanear QR code&rdquo;</li>
            <li>Escaneie o código abaixo</li>
            <li>Digite o código de 6 dígitos que aparecer pra confirmar</li>
          </ol>
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <img src={setupData.qr_code_data_uri} alt="QR code 2FA" className="w-44 h-44 border-4 border-white rounded-lg shadow" data-testid="2fa-qr-img" />
            <div className="flex-1 space-y-2">
              <p className="text-xs text-gray-600 dark:text-gray-300">Se não puder escanear, digite este secret manualmente no app:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-white dark:bg-gray-700 p-2 rounded border font-mono tracking-wider" data-testid="2fa-secret">{setupData.secret}</code>
                <button onClick={copySecret} className="p-1.5 hover:bg-white/50 rounded text-blue-600"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 pt-2">Digite o código de 6 dígitos:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  pattern="\d{6}"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="flex-1 px-3 py-2 text-lg font-mono text-center tracking-widest border-2 border-blue-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  placeholder="000000"
                  data-testid="2fa-code-input"
                  autoFocus
                />
                <button onClick={confirmEnable} disabled={enabling || code.length < 6} className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50" data-testid="2fa-confirm-btn">
                  {enabling ? '...' : 'Ativar'}
                </button>
              </div>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-[11px] text-amber-800">Guarde o secret em local seguro caso perca o celular. Sem ele você não consegue entrar.</p>
          </div>
        </div>
      )}

      {/* Disable modal */}
      {showDisableModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm p-5 space-y-3">
            <h4 className="font-bold text-base flex items-center gap-2"><ShieldOff className="w-5 h-5 text-red-600" /> Desativar 2FA</h4>
            <p className="text-xs text-gray-600">Confirme sua senha pra desativar a autenticação de dois fatores.</p>
            <input type="password" autoFocus value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} placeholder="Sua senha" className="w-full px-3 py-2 text-sm border rounded" data-testid="2fa-disable-pwd" />
            <input type="text" maxLength={6} value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))} placeholder="Código 2FA (opcional)" className="w-full px-3 py-2 text-sm border rounded font-mono text-center tracking-widest" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowDisableModal(false)} className="px-3 py-1.5 text-sm rounded hover:bg-gray-200">Cancelar</button>
              <button onClick={confirmDisable} className="px-4 py-1.5 text-sm font-semibold bg-red-600 text-white rounded hover:bg-red-700" data-testid="2fa-disable-confirm">Desativar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
